/**
 * Google Analytics 4 (GA4) Integration
 * 
 * This module provides typed event tracking for GA4.
 * Requires VITE_GA_MEASUREMENT_ID to be configured.
 */

// GA4 Measurement ID - configure in environment
export const GA_MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID || '';

// Check if gtag is available
declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
    GA_MEASUREMENT_ID?: string;
  }
}

// Initialize GA4 with measurement ID (loads gtag script and config from env)
export function initializeGA4(): void {
  if (!GA_MEASUREMENT_ID) {
    if (import.meta.env.DEV) {
      console.warn('[GA4] No measurement ID configured (VITE_GA_MEASUREMENT_ID)');
    }
    return;
  }

  window.GA_MEASUREMENT_ID = GA_MEASUREMENT_ID;

  // Load gtag.js with measurement ID from env (no hardcoded ID); defer to avoid blocking render
  if (typeof window.gtag === 'undefined') return;
  const loadGA = (): void => {
    if ((window as unknown as { gaLoaded?: boolean }).gaLoaded) {
      applyGA4Config();
      return;
    }
    (window as unknown as { gaLoaded?: boolean }).gaLoaded = true;
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
    script.onload = () => {
      window.gtag('js', new Date());
      applyGA4Config();
    };
    document.head.appendChild(script);
  };
  if (typeof window.requestIdleCallback !== 'undefined') {
    window.requestIdleCallback(loadGA, { timeout: 3000 });
  } else if (document.readyState === 'complete') {
    setTimeout(loadGA, 100);
  } else {
    window.addEventListener('load', () => setTimeout(loadGA, 100));
  }
}

function applyGA4Config(): void {
  if (typeof window.gtag !== 'function') return;
  window.gtag('config', GA_MEASUREMENT_ID, {
    send_page_view: false, // We'll send manually for SPA
  });
  const utmParams = getStoredUTMParams();
  if (Object.keys(utmParams).length > 0) {
    window.gtag('set', 'user_properties', utmParams);
  }
}

// Get UTM params from localStorage (captured during landing)
function getStoredUTMParams(): Record<string, string> {
  try {
    const stored = localStorage.getItem('utm_params');
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (e) {
    // Ignore parse errors
  }
  return {};
}

// Type-safe event names
export type GA4Event = 
  | 'page_view'
  | 'signup_complete'
  | 'login'
  | 'survey_complete'
  | 'prediction_placed'
  | 'referral_shared'
  | 'jackpot_entry'
  | 'campaign_complete'
  | 'profile_updated'
  | 'protect_scan_completed';

// Event parameters type
interface EventParams {
  [key: string]: string | number | boolean | undefined;
}

/**
 * Track a GA4 event
 */
export function trackEvent(eventName: GA4Event, params?: EventParams): void {
  if (!GA_MEASUREMENT_ID || typeof window.gtag !== 'function') {
    if (import.meta.env.DEV) {
      console.log('[GA4 Mock]', eventName, params);
    }
    return;
  }

  window.gtag('event', eventName, {
    ...params,
    send_to: GA_MEASUREMENT_ID,
  });
}

/**
 * Track a page view
 */
export function trackPageView(path: string, title?: string): void {
  trackEvent('page_view', {
    page_path: path,
    page_title: title || document.title,
  });
}

/**
 * Set user ID for cross-device tracking
 */
export function setUserId(userId: string | null): void {
  if (!GA_MEASUREMENT_ID || typeof window.gtag !== 'function') return;

  if (userId) {
    window.gtag('set', { user_id: userId });
  }
}

// Convenience methods for typed conversion events
export const ga4 = {
  signupComplete: (method: string) => 
    trackEvent('signup_complete', { method }),
  
  login: (method: string) => 
    trackEvent('login', { method }),
  
  surveyComplete: (surveyId: string, entriesEarned: number) => 
    trackEvent('survey_complete', { survey_id: surveyId, entries_earned: entriesEarned }),
  
  predictionPlaced: (predictionId: string, stake: number, optionId: string) => 
    trackEvent('prediction_placed', { prediction_id: predictionId, stake, option_id: optionId }),
  
  referralShared: (platform: string) => 
    trackEvent('referral_shared', { platform }),
  
  jackpotEntry: (drawId: string, entries: number) => 
    trackEvent('jackpot_entry', { draw_id: drawId, entries }),
  
  campaignComplete: (campaignId: string, reward: number) => 
    trackEvent('campaign_complete', { campaign_id: campaignId, reward }),
  
  profileUpdated: (fields: string[]) => 
    trackEvent('profile_updated', { updated_fields: fields.join(',') }),

  protectScanCompleted: (hasBreaches: boolean, breachCount: number) =>
    trackEvent('protect_scan_completed', { has_breaches: hasBreaches, breach_count: breachCount }),
};
